import { useQueries } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { request } from '../../../shared/api/client';
import { ChartCard, HBars, Legend, LineSeries, StackedBar } from '../../../shared/ui/Charts';
import { currencyClass } from '../../../shared/ui/currency-color';
import { EmptyState } from '../../../shared/ui/EmptyState';
import { ErrorMessage } from '../../../shared/ui/ErrorMessage';
import { Loading } from '../../../shared/ui/Loading';
import { PageHeader } from '../../../shared/ui/PageHeader';
import { type ProfitReport, useProfitReport } from '../api/useReports';

// AnalyticsPage — visual read of the profit report.
//
// Period tabs mirror the dashboard (D-027: single period grammar).
// The line chart is built from N daily useProfitReport calls (one per
// day in the period) so the series shows real daily net-profit values,
// not a single aggregate. Donut and Gauge removed per D-027 brief.

type Period = 'today' | '7d' | '30d' | 'month' | 'custom';

function fmtISO(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function todayISO(): string {
  return fmtISO(new Date());
}

function periodRange(
  period: Period,
  customFrom: string,
  customTo: string,
): { from: string; to: string } {
  const now = new Date();
  if (period === 'today') return { from: todayISO(), to: todayISO() };
  if (period === '7d') {
    const d = new Date(now);
    d.setDate(d.getDate() - 6);
    return { from: fmtISO(d), to: todayISO() };
  }
  if (period === '30d') {
    const d = new Date(now);
    d.setDate(d.getDate() - 29);
    return { from: fmtISO(d), to: todayISO() };
  }
  if (period === 'month') {
    const d = new Date(now);
    d.setDate(1);
    return { from: fmtISO(d), to: todayISO() };
  }
  return { from: customFrom, to: customTo };
}

// Previous period: same duration immediately before `from`.
function prevRange(from: string, to: string): { from: string; to: string } {
  const start = new Date(from + 'T00:00:00');
  const end = new Date(to + 'T00:00:00');
  const days = Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1;
  const prevEnd = new Date(start);
  prevEnd.setDate(prevEnd.getDate() - 1);
  const prevStart = new Date(prevEnd);
  prevStart.setDate(prevStart.getDate() - days + 1);
  return { from: fmtISO(prevStart), to: fmtISO(prevEnd) };
}

function generateDays(from: string, to: string): string[] {
  const days: string[] = [];
  const cur = new Date(from + 'T00:00:00');
  const end = new Date(to + 'T00:00:00');
  while (cur <= end) {
    days.push(fmtISO(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return days;
}

function fmt(s: string): string {
  const n = parseFloat(s);
  if (!Number.isFinite(n)) return s;
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function AnalyticsPage() {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<Period>('month');
  const [customFrom, setCustomFrom] = useState(fmtISO(new Date()));
  const [customTo, setCustomTo] = useState(fmtISO(new Date()));

  const PERIODS: { key: Period; label: string }[] = [
    { key: 'today', label: t('dashboard.period_today') },
    { key: '7d', label: t('dashboard.period_7d') },
    { key: '30d', label: t('dashboard.period_30d') },
    { key: 'month', label: t('dashboard.period_month') },
    { key: 'custom', label: t('analytics.period_custom') },
  ];

  const { from, to } = periodRange(period, customFrom, customTo);
  const { from: prevFrom, to: prevTo } = prevRange(from, to);

  const currentDays = useMemo(() => generateDays(from, to), [from, to]);
  const prevDays = useMemo(() => generateDays(prevFrom, prevTo), [prevFrom, prevTo]);

  // N daily API calls for the line chart — parallelized via useQueries.
  const currentResults = useQueries({
    queries: currentDays.map((day) => ({
      queryKey: ['reports', 'profit', { from: day, to: day }],
      queryFn: () => request<ProfitReport>(`/reports/profit?from=${day}&to=${day}`),
    })),
  });

  const prevResults = useQueries({
    queries: prevDays.map((day) => ({
      queryKey: ['reports', 'profit', { from: day, to: day }],
      queryFn: () => request<ProfitReport>(`/reports/profit?from=${day}&to=${day}`),
    })),
  });

  // Single aggregate call for stacked bar + HBars (period totals).
  const profit = useProfitReport({ from, to });

  // ----- derived series --------------------------------------------------

  const lineLoading =
    currentResults.some((r) => r.isLoading) || prevResults.some((r) => r.isLoading);
  const lineError =
    currentResults.find((r) => r.error)?.error ?? prevResults.find((r) => r.error)?.error;

  const lineSeries = useMemo(() => {
    if (lineLoading) return [];
    return [
      {
        label: t('analytics.this_period'),
        points: currentDays.map((date, i) => ({
          date,
          value: parseFloat(currentResults[i]?.data?.netProfitMru ?? '0'),
        })),
        className: 'currency--green',
      },
      {
        label: t('analytics.prev_period'),
        points: prevDays.map((date, i) => ({
          date,
          value: parseFloat(prevResults[i]?.data?.netProfitMru ?? '0'),
        })),
        className: 'currency--purple',
      },
    ];
  }, [lineLoading, currentDays, prevDays, currentResults, prevResults, t]);

  const profitBars = useMemo(() => {
    if (!profit.data) return [];
    return profit.data.byCurrency
      .map((r) => ({
        label: r.currencyCode,
        value: parseFloat(r.grossProfitMru),
        displayValue: `${fmt(r.grossProfitMru)} MRU`,
        className: currencyClass(r.currencyCode),
      }))
      .filter((b) => b.value !== 0)
      .sort((a, b) => b.value - a.value);
  }, [profit.data]);

  const stackedSegments = useMemo(() => {
    if (!profit.data) return [];
    return [
      {
        label: t('analytics.gross_profit'),
        value: Math.max(0, parseFloat(profit.data.grossProfitMru)),
        className: 'currency--green',
      },
      {
        label: t('analytics.purchase_pnl'),
        value: Math.max(0, parseFloat(profit.data.purchasePnlMru ?? '0')),
        className: 'currency--blue',
      },
      {
        label: t('analytics.fx_gain'),
        value: Math.max(0, parseFloat(profit.data.realizedFxGainMru)),
        className: 'currency--purple',
      },
      {
        label: t('analytics.expenses'),
        value: Math.abs(parseFloat(profit.data.expensesMru)),
        className: 'currency--orange',
      },
    ].filter((s) => s.value > 0);
  }, [profit.data, t]);

  // ----- render -----------------------------------------------------------

  return (
    <>
      <PageHeader title={t('analytics.title')} />

      {/* Period tabs */}
      <div className="period-tabs" role="tablist" aria-label={t('analytics.title')}>
        {PERIODS.map((p) => (
          <button
            key={p.key}
            type="button"
            role="tab"
            aria-selected={period === p.key}
            className={`period-tabs__item${period === p.key ? ' is-active' : ''}`}
            onClick={() => setPeriod(p.key)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Custom date inputs */}
      {period === 'custom' ? (
        <section className="filter-bar" aria-label={t('analytics.period_filter')}>
          <label>
            {t('reports.from')}
            <input
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              max={customTo}
            />
          </label>
          <label>
            {t('reports.to')}
            <input
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              min={customFrom}
            />
          </label>
        </section>
      ) : null}

      {/* Line chart — full width, outside the grid */}
      <ChartCard title={t('analytics.line_title')} style={{ marginBlockEnd: 'var(--sp-4)' }}>
        {lineLoading ? <Loading /> : null}
        {lineError ? <ErrorMessage error={lineError as Error} /> : null}
        {!lineLoading && !lineError ? (
          <>
            <LineSeries series={lineSeries} ariaLabel={t('analytics.line_title')} />
            <Legend
              items={[
                {
                  label: t('analytics.this_period'),
                  value: `${fmt(profit.data?.netProfitMru ?? '0')} MRU`,
                  className: 'currency--green',
                },
                {
                  label: t('analytics.prev_period'),
                  value: '',
                  className: 'currency--purple',
                },
              ]}
              ariaLabel={t('analytics.line_title')}
            />
          </>
        ) : null}
      </ChartCard>

      {profit.error ? <ErrorMessage error={profit.error} /> : null}

      {profit.data ? (
        <>
          <div className="chart-grid">
            {/* Profit decomposition stacked bar */}
            <ChartCard title={t('analytics.decomposition_title')}>
              {stackedSegments.length > 0 ? (
                <>
                  <StackedBar
                    segments={stackedSegments}
                    ariaLabel={t('analytics.decomposition_title')}
                  />
                  <Legend
                    items={[
                      {
                        label: t('analytics.gross_profit'),
                        value: `${fmt(profit.data.grossProfitMru)} MRU`,
                        className: 'currency--green',
                      },
                      {
                        label: t('analytics.purchase_pnl'),
                        value: `${fmt(profit.data.purchasePnlMru ?? '0')} MRU`,
                        className: 'currency--blue',
                      },
                      {
                        label: t('analytics.fx_gain'),
                        value: `${fmt(profit.data.realizedFxGainMru)} MRU`,
                        className: 'currency--purple',
                      },
                      {
                        label: t('analytics.expenses'),
                        value: `− ${fmt(profit.data.expensesMru)} MRU`,
                        className: 'currency--orange',
                      },
                    ]}
                    ariaLabel={t('analytics.decomposition_title')}
                  />
                  <p
                    style={{
                      margin: 0,
                      fontWeight: 700,
                      fontFamily: 'var(--font-mono)',
                      fontSize: '1.1rem',
                      textAlign: 'center',
                    }}
                  >
                    {fmt(profit.data.netProfitMru)} MRU
                  </p>
                </>
              ) : (
                <EmptyState icon="📊" title={t('analytics.no_data')} />
              )}
            </ChartCard>

            {/* Gross profit by currency */}
            <ChartCard title={t('analytics.profit_by_currency_title')}>
              {profitBars.length === 0 ? (
                <EmptyState icon="📈" title={t('analytics.no_currency_profit')} />
              ) : (
                <HBars bars={profitBars} ariaLabel={t('analytics.profit_by_currency_title')} />
              )}
            </ChartCard>
          </div>

          <div className="notice notice--info analytics-note" role="note">
            <strong>{t('analytics.methodology_title')}</strong>
            <p style={{ margin: '8px 0 0' }}>{t('analytics.methodology_body')}</p>
          </div>
        </>
      ) : null}

      {!profit.data && profit.isLoading ? <Loading /> : null}
    </>
  );
}
